import { showToast } from '../../lib/toast.js';
import {
  type SettingsCtx, DEFAULT_SHOP, loadOptions, createOptionGroup, updateOptionGroup, deleteOptionGroup,
  createOptionValue, updateOptionValue, deleteOptionValue, uploadOptionValueImage,
  loadShopPublicCreds,
  type GambioOptionGroup, type OptLangMap,
} from './data.js';

/**
 * Nach dem Speichern die Bezeichnungen und die Reihenfolge dieser Option nach Vario nachziehen
 * lassen (Job `gambio.sync_options_to_vario`). Absichtlich nicht abgewartet und ohne eigenen Toast:
 * der Nachzug ist Nacharbeit, nicht Teil des Speicherns, und betrifft nur bereits zugeordnete
 * Optionen. Ein Fehler landet in der Konsole — ein „gespeichert" soll er nicht in Frage stellen.
 */
function pushOptionToVario(sctx: SettingsCtx, gambioOptionId: string): void {
  void sctx.supabase
    .rpc('gambio_enqueue_option_sync_to_vario', { _shop_key: DEFAULT_SHOP, _gambio_option_ids: [gambioOptionId] })
    .then(({ error }: { error: { message: string } | null }) => {
      if (error) console.warn('[options] Vario-Nachzug nicht angestoßen:', error.message);
    });
}

const TYPES = ['Dropdown', 'Image', 'Radio', 'Text', 'BoxedText'];

/**
 * Shop-Basis für die Bild-Vorschau. Gambio liefert am Optionswert nur den Dateinamen, nicht die
 * Adresse.
 *
 * Der Ordner ist am Shop nachgesehen, nicht geraten (25.08.2026, gx5demo): Optionsbilder liegen
 * unter `images/product_images/option_images/` — NICHT bei den Produktbildern in
 * `original_images/`, wo meine erste Fassung sie vermutet hatte (deshalb blieb die Vorschau leer).
 * Belegt an der Produktseite des Shops, die genau diesen Pfad ausgibt, und per HTTP-Abruf beider
 * Dateien (200 vs. 404/500). `original_images` bleibt als zweiter Kandidat stehen, falls eine
 * ältere Shop-Version die Datei doch dort ablegt; lädt keiner, tritt der Dateiname als Text an
 * die Stelle des Bildes.
 */
let SHOP_BASE = '';
const IMG_DIRS = ['images/product_images/option_images', 'images/product_images/original_images'];
function imageCandidates(file: string): string[] {
  if (!SHOP_BASE || !file) return [];
  return IMG_DIRS.map((d) => `${SHOP_BASE}/${d}/${encodeURIComponent(file)}`);
}
const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
let LANGS: string[] = ['de', 'en'];

function langInputs(prefix: string, labels: OptLangMap): string {
  return LANGS.map((l) => `<label class="opt-lang"><span class="opt-lang-code">${esc(l.toUpperCase())}</span>
    <input class="inp" data-lang="${esc(l)}" data-prefix="${esc(prefix)}" value="${esc(labels[l] ?? '')}" /></label>`).join('');
}
function collectLangs(scope: HTMLElement, prefix: string): OptLangMap {
  const out: OptLangMap = {};
  scope.querySelectorAll<HTMLInputElement>(`input[data-prefix="${prefix}"][data-lang]`).forEach((i) => { out[i.dataset['lang']!] = i.value; });
  return out;
}

/** Nach dem Rendern eine Gruppe sichtbar halten: `newest` = zuletzt angelegte, `gid` = bestimmte. */
type FocusTarget = { newest: true } | { gid: string } | null;

export async function viewOptionen(body: HTMLElement, sctx: SettingsCtx, focus: FocusTarget = null): Promise<void> {
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'Gambio-Optionen mehrsprachig verwalten';
  body.innerHTML = `<div class="hint">Optionen werden geladen …</div>`;
  let data;
  try { data = await loadOptions(sctx, DEFAULT_SHOP); }
  catch (e) { body.innerHTML = `<div class="hint">Optionen nicht ladbar: ${esc((e as Error).message)}</div>`; return; }
  LANGS = data.languages.length ? data.languages : ['de', 'en'];

  // Shop-Basis nur für die Vorschau. Ist sie nicht lesbar, bleibt es beim Dateinamen als Text —
  // die Optionspflege selbst hängt nicht daran.
  try {
    const creds = await loadShopPublicCreds(sctx, DEFAULT_SHOP);
    const raw = (creds?.base_url ?? '').trim();
    SHOP_BASE = raw ? (/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).replace(/\/+$/, '') : '';
  } catch { SHOP_BASE = ''; }

  body.innerHTML = `<div class="opt-root">
    <div class="map-toolbar"><span class="map-stat"><b>${data.options.length}</b> Optionsgruppen</span>
      <button type="button" class="v-button" id="opt-add-group" style="margin-left:auto;"><span class="v-icon fa-regular fa-plus"></span> Optionsgruppe hinzufügen</button></div>
    <div id="opt-list">${data.options.map((o) => renderGroup(o)).join('')}</div>
  </div>`;

  wire(body, sctx);
  wireThumbFallback(body);
  if (focus) applyFocus(body, focus);
}

/** Hält nach einem Reload die relevante Gruppe im Blick, damit der Nutzer nicht scrollen/suchen muss.
 *  `newest`: die zuletzt angelegte Gruppe (höchste Gambio-Options-ID) + Fokus auf ihr erstes Namensfeld.
 *  `gid`: eine bestimmte Gruppe (z. B. die gerade bearbeitete) sichtbar machen. */
function applyFocus(body: HTMLElement, focus: FocusTarget): void {
  const root = body.querySelector<HTMLElement>('.opt-root');
  if (!root || !focus) return;
  let target: HTMLElement | null = null;
  if ('newest' in focus) {
    let maxId = -Infinity;
    root.querySelectorAll<HTMLElement>('.opt-group').forEach((g) => {
      const id = Number(g.dataset['gid']);
      if (Number.isFinite(id) && id > maxId) { maxId = id; target = g; }
    });
  } else {
    target = root.querySelector<HTMLElement>(`.opt-group[data-gid="${CSS.escape(focus.gid)}"]`);
  }
  if (!target) return;
  (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
  if ('newest' in focus) (target as HTMLElement).querySelector<HTMLInputElement>('.opt-lang .inp')?.focus();
}

/** Nächster freier sortOrder = größter vorhandener Wert + 1 (statt fester 99). */
function nextSortOrder(inputs: NodeListOf<HTMLInputElement>): number {
  let max = 0;
  inputs.forEach((i) => { const n = Number(i.value); if (Number.isFinite(n) && n > max) max = n; });
  return max + 1;
}

function renderGroup(o: GambioOptionGroup): string {
  const gid = esc(o.optionId);
  const typeOpts = TYPES.map((t) => `<option value="${t}"${t === o.type ? ' selected' : ''}>${t}</option>`).join('');
  const valueRows = o.values
    .slice().sort((a, b) => a.sortOrder - b.sortOrder)
    .map((v) => {
      const vid = esc(v.valueId);
      const cands = v.image ? imageCandidates(v.image) : [];
      const preview = v.image
        ? (cands.length
          ? `<span class="opt-thumb-wrap"><img class="opt-thumb" alt="${esc(v.image)}" title="${esc(v.image)}" src="${esc(cands[0]!)}" data-thumb-alt="${esc(cands.slice(1).join('|'))}" /></span>`
          : `<span class="chip ok">${esc(v.image)}</span>`)
        : `<span class="chip miss">kein Bild</span>`;
      const img = o.type === 'Image'
        ? `<td class="opt-img"><div class="opt-img-cell">${preview}
             <input type="file" accept="image/png,image/jpeg,image/gif" data-imgfor="${vid}" style="display:none;" />
             <button type="button" class="v-button" data-act="pick-img" data-vid="${vid}"><span class="v-icon fa-regular fa-image"></span> Bild…</button></div></td>`
        : '';
      // Ausgangswerte an der Zeile: der Speichern-Knopf der Gruppe schreibt später NUR, was
      // sich wirklich geändert hat — sonst geht jeder Klick als voller Schreiblauf über alle
      // Werte an Gambio.
      // Schlüsselreihenfolge MUSS der von `collectLangs` entsprechen (Sprachen in LANGS-Ordnung),
      // sonst unterscheiden sich die JSON-Texte trotz gleicher Werte und jede Zeile gilt als geändert.
      const origLabels: OptLangMap = {};
      for (const l of LANGS) origLabels[l] = v.labels[l] ?? '';
      const orig = esc(JSON.stringify({
        sortOrder: v.sortOrder, labels: origLabels, modelNumber: v.modelNumber, price: v.price, weight: v.weight,
      }));
      return `<tr data-vid="${vid}" data-orig="${orig}">
        <td>${langInputs(`val-${vid}`, v.labels)}</td>
        <td><input class="inp" data-f="model" value="${esc(v.modelNumber)}" title="Artikelnummer (steuert zugleich die Vario-Zuordnung)" /></td>
        <td><input class="inp" data-f="price" type="number" step="0.01" value="${v.price}" /></td>
        <td><input class="inp" data-f="weight" type="number" step="0.001" value="${v.weight}" /></td>
        <td><input class="inp" data-f="sort" type="number" step="1" value="${v.sortOrder}" style="width:64px;" /></td>
        ${img}
        <td class="opt-actions"><div class="opt-actions-inner"><button type="button" class="v-button" data-act="del-value" data-vid="${vid}"><span class="v-icon fa-regular fa-trash"></span> Löschen</button></div></td>
      </tr>`;
    }).join('');
  return `<div class="opt-group" data-gid="${gid}">
    <div class="opt-head">
      <span class="opt-head-title">${esc(o.labels[LANGS[0]!] ?? o.optionId)}</span>
      <span class="opt-head-meta">${esc(o.type)} · ${o.values.length} Wert${o.values.length === 1 ? '' : 'e'}</span>
      <button type="button" class="v-button" data-act="del-group" data-gid="${gid}"><span class="v-icon fa-regular fa-trash"></span> Löschen</button>
      <button type="button" class="v-button v-button--save" data-act="save-group" data-gid="${gid}"><span class="v-icon fa-regular fa-floppy-disk"></span> Speichern</button>
    </div>
    <div class="frow"><div class="field"><label>Anzeigetyp</label><div class="selwrap"><select class="sel" data-f="type">${typeOpts}</select></div></div>
      <div class="field"><label>Sortierung</label><input class="inp" data-f="sort" type="number" step="1" value="${o.sortOrder}" style="width:80px;" /></div></div>
    <div class="frow"><div class="field full"><label>Name je Sprache</label>${langInputs(`grp-${gid}`, o.labels)}</div></div>
    <table class="maptable"><thead><tr><th>Name</th><th>Artikelnr.</th><th>Preis</th><th>Gewicht</th><th>Sort.</th>${o.type === 'Image' ? '<th>Bild</th>' : ''}<th></th></tr></thead>
      <tbody>${valueRows || `<tr><td colspan="7" style="text-align:center;color:#aab;padding:16px;">Noch keine Werte.</td></tr>`}</tbody></table>
    <button type="button" class="v-button" data-act="add-value" data-gid="${gid}" style="margin-top:8px;"><span class="v-icon fa-regular fa-plus"></span> Wert hinzufügen</button>
  </div>`;
}

/**
 * Lädt ein Vorschaubild nicht, wird der nächste Kandidat probiert; ist keiner mehr übrig,
 * tritt der Dateiname als Chip an seine Stelle. Kein `onerror` im Markup — Inline-Handler
 * sind im Vario-iframe nicht verlässlich.
 */
function wireThumbFallback(body: HTMLElement): void {
  body.querySelectorAll<HTMLImageElement>('img.opt-thumb').forEach((img) => {
    img.onerror = () => {
      const rest = (img.dataset['thumbAlt'] ?? '').split('|').filter(Boolean);
      const next = rest.shift();
      if (next) { img.dataset['thumbAlt'] = rest.join('|'); img.src = next; return; }
      const chip = document.createElement('span');
      chip.className = 'chip ok';
      chip.textContent = img.alt;
      // Den ganzen Rahmen ersetzen, nicht nur das Bild — sonst bliebe ein leeres 40er-Kästchen stehen.
      (img.closest('.opt-thumb-wrap') ?? img).replaceWith(chip);
    };
  });
}

function reload(body: HTMLElement, sctx: SettingsCtx, focus: FocusTarget = null): void { void viewOptionen(body, sctx, focus); }

async function confirmTwoStep(btn: HTMLButtonElement): Promise<boolean> {
  if (btn.dataset['armed'] === '1') return true;
  const prev = btn.innerHTML;
  btn.dataset['armed'] = '1';
  btn.innerHTML = '<span class="v-icon fa-regular fa-trash"></span> Wirklich löschen?';
  btn.classList.add('v-button--danger');
  window.setTimeout(() => { if (btn.isConnected) { btn.dataset['armed'] = '0'; btn.innerHTML = prev; btn.classList.remove('v-button--danger'); } }, 4000);
  return false;
}

function valueInput(tr: HTMLElement, vid: string): { sortOrder: number; labels: OptLangMap; modelNumber: string; price: number; weight: number } {
  return {
    sortOrder: Number(tr.querySelector<HTMLInputElement>('[data-f="sort"]')!.value) || 0,
    labels: collectLangs(tr, `val-${vid}`),
    modelNumber: tr.querySelector<HTMLInputElement>('[data-f="model"]')!.value,
    price: Number(tr.querySelector<HTMLInputElement>('[data-f="price"]')!.value) || 0,
    weight: Number(tr.querySelector<HTMLInputElement>('[data-f="weight"]')!.value) || 0,
  };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });
}

function wire(body: HTMLElement, sctx: SettingsCtx): void {
  const root = body.querySelector<HTMLElement>('.opt-root')!;
  root.querySelector<HTMLButtonElement>('#opt-add-group')!.onclick = async () => {
    const sortOrder = nextSortOrder(root.querySelectorAll<HTMLInputElement>('.opt-group .frow input[data-f="sort"]'));
    try { await createOptionGroup(sctx, DEFAULT_SHOP, { type: 'Dropdown', sortOrder, labels: { [LANGS[0]!]: 'Neue Option' }, adminLabels: {} }); showToast('Optionsgruppe angelegt.', 'success'); reload(body, sctx, { newest: true }); }
    catch (e) { showToast(`Anlegen fehlgeschlagen: ${(e as Error).message}`, 'error'); }
  };

  root.addEventListener('click', async (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!btn) return;
    const act = btn.dataset['act'];
    const group = btn.closest<HTMLElement>('.opt-group');
    const gid = group?.dataset['gid'] ?? '';
    try {
      if (act === 'save-group') {
        // EIN Knopf je Gruppe: schreibt die Gruppe UND alle geänderten Werte. Dass dahinter
        // mehrere API-Aufrufe liegen, ist Technik und kein Grund für einen Knopf je Zeile.
        const type = group!.querySelector<HTMLSelectElement>('select[data-f="type"]')!.value;
        const sortOrder = Number(group!.querySelector<HTMLInputElement>('.frow input[data-f="sort"]')!.value) || 0;
        const labels = collectLangs(group!, `grp-${gid}`);
        btn.disabled = true;
        try {
          await updateOptionGroup(sctx, DEFAULT_SHOP, gid, { type, sortOrder, labels, adminLabels: {} });
          let written = 0;
          for (const tr of Array.from(group!.querySelectorAll<HTMLTableRowElement>('tr[data-vid]'))) {
            const vid = tr.dataset['vid']!;
            const next = valueInput(tr, vid);
            if (tr.dataset['orig'] === JSON.stringify(next)) continue;
            await updateOptionValue(sctx, DEFAULT_SHOP, gid, vid, next);
            tr.dataset['orig'] = JSON.stringify(next);
            written += 1;
          }
          // Kein Reload: Position/Fokus bleiben erhalten. Nur den Gruppentitel in-place nachziehen.
          const title = group!.querySelector('.opt-head-title');
          if (title) title.textContent = labels[LANGS[0]!] ?? gid;
          showToast(written > 0
            ? `Gruppe und ${written} Wert${written === 1 ? '' : 'e'} gespeichert.`
            : 'Gruppe gespeichert.', 'success');
          void pushOptionToVario(sctx, gid);
        } finally {
          btn.disabled = false;
        }
      } else if (act === 'del-group') {
        if (!(await confirmTwoStep(btn))) return;
        await deleteOptionGroup(sctx, DEFAULT_SHOP, gid); showToast('Gruppe gelöscht.', 'success'); reload(body, sctx);
      } else if (act === 'add-value') {
        const sortOrder = nextSortOrder(group!.querySelectorAll<HTMLInputElement>('tr[data-vid] input[data-f="sort"]'));
        await createOptionValue(sctx, DEFAULT_SHOP, gid, { sortOrder, labels: { [LANGS[0]!]: 'Neuer Wert' } });
        showToast('Wert angelegt.', 'success'); reload(body, sctx, { gid });
      } else if (act === 'del-value') {
        if (!(await confirmTwoStep(btn))) return;
        const vid = btn.closest<HTMLElement>('tr')!.dataset['vid']!;
        await deleteOptionValue(sctx, DEFAULT_SHOP, gid, vid); showToast('Wert gelöscht.', 'success'); reload(body, sctx, { gid });
      } else if (act === 'pick-img') {
        const vid = btn.dataset['vid']!;
        group!.querySelector<HTMLInputElement>(`input[type="file"][data-imgfor="${vid}"]`)!.click();
      }
    } catch (e) { showToast(`Fehler: ${(e as Error).message}`, 'error'); }
  });

  root.addEventListener('change', async (ev) => {
    const inp = ev.target as HTMLInputElement;
    if (inp.type !== 'file' || !inp.dataset['imgfor']) return;
    const file = inp.files?.[0]; if (!file) return;
    const group = inp.closest<HTMLElement>('.opt-group')!; const gid = group.dataset['gid']!; const vid = inp.dataset['imgfor']!;
    try {
      const base64 = await fileToBase64(file);
      await uploadOptionValueImage(sctx, DEFAULT_SHOP, gid, vid, file.name, file.type || 'image/png', base64);
      showToast('Bild hochgeladen.', 'success'); reload(body, sctx, { gid });
    } catch (e) { showToast(`Upload fehlgeschlagen: ${(e as Error).message}`, 'error'); }
  });
}
